// @flow
/* eslint-disable no-shadow */
import React, { type ComponentType } from 'react';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import type { Country } from '../types/country';

const fieldMappings = {
  address1: 'FormattedLine1',
  address2: 'FormattedLine2',
  town: 'City',
  region: 'Province',
  postCode: 'PostalCode',
};

/**
 * Initiates a new instance of the address lookup service provided by Loquate (https://www.loqate.com)
 *
 * @param {Object} config
 * @returns {Object}
 */
function initiateLookup(config) {
  const { pca, formToInit, formattedCountries, apiKey } = config;

  const fields = [
    { element: `${formToInit}__search`, field: 'Search' },
    {
      element: `${formToInit}__address1`,
      field: fieldMappings.address1,
      mode: pca.fieldMode.POPULATE,
    },
    {
      element: `${formToInit}__address2`,
      field: fieldMappings.address2,
      mode: pca.fieldMode.POPULATE,
    },
    {
      element: `${formToInit}__town`,
      field: fieldMappings.town,
      mode: pca.fieldMode.POPULATE,
    },
    {
      element: `${formToInit}__region`,
      field: fieldMappings.region,
      mode: pca.fieldMode.POPULATE,
    },
    {
      element: `${formToInit}__postcode`,
      field: fieldMappings.postCode,
      mode: pca.fieldMode.POPULATE,
    },
    {
      element: `${formToInit}__country`,
      field: 'CountryName',
      mode: pca.fieldMode.COUNTRY,
    },
  ];

  return new pca.Address(fields, {
    key: apiKey,
    search: {
      countries: formattedCountries.map(country => country.isoCode).join(','),
    },
  });
}

type Props = {
  pcaPredict: {
    apiKey: string,
    css: Url,
    scripts: {
      lib: Url,
      mock?: Url,
    },
  },
  countries: Array<Country>,
  // address param is an object returned by Loquate
  // TODO: Specify Object type
  onPopulate: (string, Object, Array<Country>) => void,
};

type State = {
  pcaReady: boolean,
  formReady: boolean,
};

type WrappedComponentType = ComponentType<*> & {
  populateStore?: (store: {}) => void,
  WrappedComponent?: WrappedComponentType,
};

/**
 * @param {Component} Component
 * @param {Object} componentProps
 * @returns {*}
 */
export function requireAddressLookup(
  Component: WrappedComponentType,
  componentProps: { formName: string, restrictCountries?: boolean }
): ComponentType<Props> {
  const { formName, restrictCountries } = componentProps;

  class AddressLookupContainer extends React.Component<Props, State> {
    state = {
      pcaReady: false,
      formReady: false,
    };

    /**
     *
     */
    componentDidMount() {
      if (this.hasPcaLoaded() && !this.state.pcaReady) {
        this.onPcaLoad(formName);
      }

      if (this.hasPcaLoaded() && !this.formReady) {
        this.initialisePcaForm(formName);
      }
    }

    /**
     * @param {Object} nextProps
     * @param {Object} nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps, nextState) {
      const { pcaReady, formReady } = nextState;
      if (pcaReady && !formReady) {
        this.initialisePcaForm(formName);
      }

      return pcaReady && !formReady;
    }

    onPcaLoad = formToInit => {
      this.initialisePcaForm(formToInit);
    };

    initialisePcaForm = formToInit => {
      const pca = window && window.pca;
      const formattedCountries = this.props.countries.map(country =>
        country.isoCode === 'UK' ? { ...country, isoCode: 'GB' } : country
      );
      const control = initiateLookup({
        pca,
        formToInit,
        formattedCountries,
        apiKey: this.props.pcaPredict.apiKey,
      });
      // TODO: flow type address
      control.listen('populate', address => {
        this.props.onPopulate(formToInit, address, formattedCountries);
      });

      this.setState({ formReady: true });

      const browserIsChrome = !!window.chrome;
      const searchInput = window.document.querySelector(
        `#${formToInit}__search`
      );
      if (searchInput && browserIsChrome) {
        // On Chrome we are setting searchInput.autocomplete to be a random string. This is to prevent the search field
        // from being populated with the first line of the customer's address when they use browser auto-complete.
        // Unfortunately the values "off" and "false" are ignored by this browser. Gitlab #1390, #2489
        searchInput.autocomplete = 'bravissimo';
      }
    };

    initialisePcaScripts = (newState, pcaScripts) => {
      if (!this.hasPcaLoaded()) {
        const pcaScript = (newState.scriptTags || []).find(
          script =>
            script && script.src && script.src.indexOf(pcaScripts.lib) > -1
        );
        if (pcaScript) {
          const scriptTag = window.document.querySelector(
            `script[src="${pcaScript.src}"]`
          );
          const originalOnload = scriptTag.onload ? scriptTag.onload : () => {};
          scriptTag.onload = () => {
            originalOnload();
            this.onPcaLoad(formName);
            this.setState({ pcaReady: true });
          };
        }
      }
    };

    hasPcaLoaded = () => !!(window && window.pca);

    /**
     * @returns {*}
     */
    render() {
      const pcaScripts = this.props.pcaPredict.scripts;
      return (
        <div>
          <Helmet
            script={Object.keys(pcaScripts).map(type => ({
              src: pcaScripts[type],
              type: 'text/javascript',
            }))}
            link={[
              {
                href: '/pca/css/pca.css',
                type: 'text/css',
                rel: 'stylesheet',
              },
            ]}
            onChangeClientState={newState =>
              this.initialisePcaScripts(newState, pcaScripts)
            }
          />
          <Component {...componentProps} />
        </div>
      );
    }
  }

  const mapStateToProps = state => ({
    pcaPredict: state.app.config.pcaPredict,
    countries: restrictCountries
      ? state.countries.data.filter(country => country.deliveryAllowed)
      : state.countries.data,
  });

  const mapDispatchToProps = dispatch => ({
    onPopulate: (name, address, countries) => {
      const country =
        countries.find(c => c.isoCode === address.CountryIso2) || {};
      // Due to the way the below loop works, any redux form that makes use of the address-field-set
      // must have its address field names begin with "address.".
      Object.keys(fieldMappings).forEach(mapping => {
        dispatch(
          change(name, `address.${mapping}`, address[fieldMappings[mapping]])
        );
      });
      dispatch(change(name, 'address.countryId', country.id));
      dispatch(change(name, 'address.search', ''));
    },
  });

  const wrappedComponent = connect(
    mapStateToProps,
    mapDispatchToProps
  )(AddressLookupContainer);

  let nextComponent = Component;

  /* Look for a populateStore method in our hierarchy so that server-side data population
   * can be carried out. This information seems not be available server/app.js:populateStore(),
   * hence we work it out here when we dynamically wrap a component. */
  do {
    wrappedComponent.populateStore = nextComponent.populateStore;
    nextComponent = nextComponent ? nextComponent.WrappedComponent : null;
  } while (wrappedComponent.populateStore === undefined && nextComponent);

  return wrappedComponent;
}

export default {
  requireAddressLookup,
};
